import { useState } from 'react';
import { FuelingRecommendation } from '../types';

interface Props {
  recommendation: FuelingRecommendation;
}

// Icon component for visual dosing
function DoseIcons({ count, type }: { count: number; type: 'carbs' | 'electrolytes' | 'caffeine' }) {
  const iconClass = type === 'carbs' ? 'ri-contrast-drop-2-fill' : type === 'electrolytes' ? 'ri-drop-fill' : 'ri-flashlight-fill';
  const displayCount = Math.min(count, 4);
  const remaining = count > 4 ? count - 4 : 0;

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {Array.from({ length: displayCount }).map((_, i) => (
        <i key={i} className={`${iconClass} text-[#00FF88] text-lg`}></i>
      ))}
      {remaining > 0 && (
        <span className="text-[#00FF88] font-bold text-sm ml-1">+{remaining}</span>
      )}
    </div>
  );
}

export default function ResultsPanel({ recommendation }: Props) {
  const hasBottles = recommendation.bottleBreakdown && recommendation.bottleBreakdown.length > 0;
  const hasPreRace = recommendation.preRace !== undefined;
  
  // Tab order logic
  const tabs: string[] = [];
  if (hasPreRace) tabs.push('prerace');
  if (hasBottles) tabs.push('bottles');
  tabs.push('doses', 'timing', 'why');
  
  const [activeTab, setActiveTab] = useState<string>(tabs[0]);

  return (
    <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-white mb-2">YOUR FUELING PLAN</h2>
        <p className="text-[#00FF88] text-lg font-bold">{recommendation.workoutLabel}</p>
        <p className="text-white/60 text-sm mt-1">{recommendation.combineLabel}</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-white/10 pb-2 overflow-x-auto">
        {hasPreRace && (
          <button
            onClick={() => setActiveTab('prerace')}
            className={`px-4 py-2 font-bold uppercase tracking-wider text-sm whitespace-nowrap transition-all ${
              activeTab === 'prerace'
                ? 'text-[#00FF88] border-b-2 border-[#00FF88]'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Pre-Race
          </button>
        )}
        {hasBottles && (
          <button
            onClick={() => setActiveTab('bottles')}
            className={`px-4 py-2 font-bold uppercase tracking-wider text-sm whitespace-nowrap transition-all ${
              activeTab === 'bottles'
                ? 'text-[#00FF88] border-b-2 border-[#00FF88]'
                : 'text-white/60 hover:text-white'
            }`}
          >
            Your Bottles
          </button>
        )}
        <button
          onClick={() => setActiveTab('doses')}
          className={`px-4 py-2 font-bold uppercase tracking-wider text-sm whitespace-nowrap transition-all ${
            activeTab === 'doses'
              ? 'text-[#00FF88] border-b-2 border-[#00FF88]'
              : 'text-white/60 hover:text-white'
          }`}
        >
          Doses
        </button>
        <button
          onClick={() => setActiveTab('timing')}
          className={`px-4 py-2 font-bold uppercase tracking-wider text-sm whitespace-nowrap transition-all ${
            activeTab === 'timing'
              ? 'text-[#00FF88] border-b-2 border-[#00FF88]'
              : 'text-white/60 hover:text-white'
          }`}
        >
          Timing
        </button>
        <button
          onClick={() => setActiveTab('why')}
          className={`px-4 py-2 font-bold uppercase tracking-wider text-sm whitespace-nowrap transition-all ${
            activeTab === 'why'
              ? 'text-[#00FF88] border-b-2 border-[#00FF88]'
              : 'text-white/60 hover:text-white'
          }`}
        >
          Why
        </button>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {/* Pre-Race Tab */}
        {activeTab === 'prerace' && recommendation.preRace && (
          <div className="space-y-4">
            <div className="bg-black/30 rounded-xl p-6 border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-moon-line text-[#00FF88] text-xl"></i>
                <h3 className="text-white font-bold uppercase tracking-wider text-sm">Night Before</h3>
              </div>
              <p className="text-white/80 text-sm leading-relaxed">{recommendation.preRace.nightBefore}</p>
            </div>

            <div className="bg-black/30 rounded-xl p-6 border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-sun-line text-[#00FF88] text-xl"></i>
                <h3 className="text-white font-bold uppercase tracking-wider text-sm">Race Morning</h3>
              </div>
              <p className="text-white/80 text-sm leading-relaxed">{recommendation.preRace.raceMorning}</p>
            </div>

            <div className="bg-black/30 rounded-xl p-6 border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-timer-line text-[#00FF88] text-xl"></i>
                <h3 className="text-white font-bold uppercase tracking-wider text-sm">Warm-Up Window</h3>
              </div>
              <p className="text-white/80 text-sm leading-relaxed">{recommendation.preRace.warmup}</p>
            </div>
          </div>
        )}

        {/* Bottles Tab */}
        {activeTab === 'bottles' && recommendation.bottleBreakdown && (
          <div className="space-y-4">
            {recommendation.bottleBreakdown.map((bottle, index) => (
              <div key={index} className="bg-black/30 rounded-xl p-6 border border-white/10">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white">{bottle.bottleLabel}</h3>
                    <p className="text-white/60 text-sm">{bottle.bottleSize}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-[#00FF88] text-sm font-bold">{bottle.startTiming}</div>
                  </div>
                </div>

                {/* Visual Icons Row */}
                <div className="flex items-center gap-6 mb-4 pb-4 border-b border-white/10">
                  {bottle.carbScoops > 0 && (
                    <div>
                      <div className="text-white/60 text-xs uppercase mb-2">Carbs</div>
                      <DoseIcons count={bottle.carbScoops} type="carbs" />
                      <div className="text-white font-bold text-sm mt-2">
                        {bottle.carbScoops} scoop{bottle.carbScoops > 1 ? 's' : ''} — <span className="text-[#00FF88]">{bottle.carbScoops * 30}g</span>
                      </div>
                    </div>
                  )}
                  {bottle.electrolyteScoops > 0 && (
                    <div>
                      <div className="text-white/60 text-xs uppercase mb-2">Electrolytes</div>
                      <DoseIcons count={bottle.electrolyteScoops} type="electrolytes" />
                      <div className="text-white font-bold text-sm mt-2">
                        {bottle.electrolyteScoops} serving{bottle.electrolyteScoops > 1 ? 's' : ''} — <span className="text-[#00FF88]">{bottle.electrolyteScoops * 800}mg sodium</span>
                      </div>
                    </div>
                  )}
                  {bottle.caffeineScoops > 0 && (
                    <div>
                      <div className="text-white/60 text-xs uppercase mb-2">Caffeine</div>
                      <DoseIcons count={bottle.caffeineScoops} type="caffeine" />
                      <div className="text-white font-bold text-sm mt-2">
                        {bottle.caffeineScoops} scoop{bottle.caffeineScoops > 1 ? 's' : ''} — <span className="text-[#00FF88]">{bottle.caffeineScoops * 30}mg caffeine</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Notes */}
                {bottle.notes.length > 0 && (
                  <div className="space-y-2">
                    {bottle.notes.map((note, noteIndex) => (
                      <div key={noteIndex} className="text-white/70 text-sm">
                        {note.includes('⚠️') ? (
                          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-amber-500/90">
                            {note}
                          </div>
                        ) : (
                          <div className="flex items-start gap-2">
                            {noteIndex === 0 ? (
                              <i className="ri-flask-line text-[#00FF88] text-sm mt-0.5"></i>
                            ) : (
                              <i className="ri-information-line text-[#00FF88] text-sm mt-0.5"></i>
                            )}
                            <span>{note}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Doses Tab */}
        {activeTab === 'doses' && (
          <>
            {/* Triathlon Segment Breakdown */}
            {recommendation.segmentBreakdown && (
              <div className="mb-6 bg-black/30 rounded-xl p-6 border border-white/10">
                <h3 className="text-lg font-bold text-white mb-4 uppercase tracking-wider">Segment Breakdown</h3>
                
                <div className="space-y-4">
                  {/* Swim */}
                  <div className="border-l-4 border-[#00FF88]/30 pl-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-bold">Swim</span>
                      <span className="text-white/60 text-sm">{recommendation.segmentBreakdown.swim.duration}</span>
                    </div>
                    <p className="text-white/70 text-sm">{recommendation.segmentBreakdown.swim.fueling}</p>
                  </div>

                  {/* Bike */}
                  <div className="border-l-4 border-[#00FF88] pl-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-bold">Bike</span>
                      <span className="text-white/60 text-sm">{recommendation.segmentBreakdown.bike.duration}</span>
                    </div>
                    <div className="text-[#00FF88] font-bold mb-1">
                      {recommendation.segmentBreakdown.bike.carbsPerHour}g carbs/hr — {recommendation.segmentBreakdown.bike.totalCarbs}g total
                    </div>
                    <p className="text-white/70 text-sm">{recommendation.segmentBreakdown.bike.electrolytes}</p>
                  </div>

                  {/* Run */}
                  <div className="border-l-4 border-[#00FF88]/60 pl-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-bold">Run</span>
                      <span className="text-white/60 text-sm">{recommendation.segmentBreakdown.run.duration}</span>
                    </div>
                    <div className="text-[#00FF88] font-bold mb-1">
                      {recommendation.segmentBreakdown.run.carbsPerHour}g carbs/hr — {recommendation.segmentBreakdown.run.totalCarbs}g total
                    </div>
                    <p className="text-white/70 text-sm">{recommendation.segmentBreakdown.run.electrolytes}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Dose Cards - NEW LAYOUT */}
            <div className="grid md:grid-cols-3 gap-4">
              {/* Carbs */}
              {recommendation.carbScoops > 0 && (
                <div className="bg-black/30 rounded-xl p-6 border border-white/10">
                  <div className="text-white/60 text-xs uppercase tracking-wider mb-3">Carbs</div>
                  
                  <DoseIcons count={recommendation.carbScoops} type="carbs" />
                  
                  <div className="text-4xl font-bold text-white my-3">{recommendation.carbScoops}</div>
                  
                  <div className="text-[#00FF88] text-lg font-bold mb-4">{recommendation.totalCarbs}g</div>
                  
                  <div className="flex justify-between items-center text-sm pt-4 border-t border-white/10">
                    <span className="text-white/60">{recommendation.carbsPerHour}g/hr</span>
                    <span className="text-white/80 text-xs">Up to 120g per bottle</span>
                  </div>
                </div>
              )}

              {/* Electrolytes */}
              <div className="bg-black/30 rounded-xl p-6 border border-white/10">
                <div className="text-white/60 text-xs uppercase tracking-wider mb-3">Electrolytes</div>
                
                <DoseIcons count={recommendation.electrolyteScoops} type="electrolytes" />
                
                <div className="text-4xl font-bold text-white my-3">{recommendation.electrolyteScoops}</div>
                
                <div className="text-[#00FF88] text-lg font-bold mb-4">{recommendation.sodiumMg}mg sodium</div>
                
                <div className="flex justify-between items-center text-sm pt-4 border-t border-white/10">
                  <span className="text-white/60">{Math.round(recommendation.sodiumMg / (recommendation.totalMinutes / 60))}mg/hr</span>
                  <span className="text-white/80 text-xs">Ironman grade</span>
                </div>
              </div>

              {/* Caffeine */}
              {recommendation.caffeineScoops > 0 && (
                <div className="bg-black/30 rounded-xl p-6 border border-white/10">
                  <div className="text-white/60 text-xs uppercase tracking-wider mb-3">Caffeine + Focus</div>
                  
                  <DoseIcons count={recommendation.caffeineScoops} type="caffeine" />
                  
                  <div className="text-4xl font-bold text-white my-3">{recommendation.caffeineScoops}</div>
                  
                  <div className="text-[#00FF88] text-lg font-bold mb-4">{recommendation.caffeineMg}mg caffeine</div>
                  
                  <div className="flex justify-between items-center text-sm pt-4 border-t border-white/10">
                    <span className="text-white/60">30mg per scoop</span>
                    <span className="text-white/80 text-xs">Timed for second half</span>
                  </div>
                </div>
              )}
            </div>

            {/* Carb Tolerance Note */}
            {recommendation.carbToleranceNote && recommendation.nextLevelCarbs && (
              <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <i className="ri-arrow-up-circle-line text-[#00FF88] text-xl mt-0.5"></i>
                  <div>
                    <div className="text-white font-bold mb-1">Your next level → {recommendation.nextLevelCarbs}g/hr</div>
                    <p className="text-white/80 text-sm">{recommendation.carbToleranceNote}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Tolerance Footer */}
            <div className="text-center text-white/50 text-xs mt-6">
              Doses shown for {recommendation.carbsPerHour >= 90 ? 'Performance/Elite' : recommendation.carbsPerHour >= 60 ? 'Intermediate' : 'Building'} tolerance. Adjust based on your gut response.
            </div>
          </>
        )}

        {/* Timing Tab */}
        {activeTab === 'timing' && (
          <div className="space-y-4">
            {recommendation.timing.map((item, index) => (
              <div key={index} className="flex gap-4 items-start">
                <div className="bg-[#00FF88] text-black font-bold text-xs px-3 py-1 rounded-full whitespace-nowrap mt-1">
                  {item.time}
                </div>
                <p className="text-white/80 text-sm flex-1">{item.note}</p>
              </div>
            ))}
          </div>
        )}

        {/* Why Tab */}
        {activeTab === 'why' && (
          <div className="space-y-4">
            {recommendation.rationale.map((reason, index) => (
              <div key={index} className="flex gap-3 items-start">
                <i className="ri-checkbox-circle-line text-[#00FF88] text-lg mt-0.5"></i>
                <p className="text-white/80 text-sm flex-1">{reason}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="mt-8 pt-8 border-t border-white/10">
        <div className="bg-gradient-to-r from-[#00FF88]/20 to-transparent rounded-xl p-6 border border-[#00FF88]/30">
          <h3 className="text-white font-bold text-lg mb-2">Ready to fuel like this?</h3>
          <p className="text-white/70 text-sm mb-4">
            Join the early access list for founding member pricing and first access to THRESH.
          </p>
          <a
            href="/#early-access"
            className="inline-block bg-[#00FF88] text-black font-bold px-6 py-3 rounded-lg hover:bg-[#00FF88]/90 transition-all whitespace-nowrap cursor-pointer"
          >
            GET EARLY ACCESS
          </a>
        </div>
      </div>
    </div>
  );
}